import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  FolderOpen,
  Share2,
  Zap,
  Lock,
  Shield,
  ChevronRight,
  Github,
  ExternalLink,
  Menu,
} from "lucide-react";
import { appData } from "../mock";
import { Button } from "./ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./ui/drawer";
import "./LandingPage.css";

const iconMap = {
  Download,
  FolderOpen,
  Share2,
  Zap,
  Lock,
  Shield,
};

export const LandingPage = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <div className="landing-page">
      {/* Navigation Header */}
      <header className="nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <img
              src={appData.logoUrl}
              alt="Status Saver Logo"
              className="logo-img"
            />
            <span className="logo-text">{appData.name}</span>
          </div>
          <nav className="nav-links">
            <a href="#features" className="nav-link">
              Features
            </a>
            <a href="#how-it-works" className="nav-link">
              How It Works
            </a>
            <a href="#download" className="nav-link">
              Download
            </a>
            <Link to="/privacy" className="nav-link">
              Privacy
            </Link>
          </nav>
          <div className="nav-actions">
            <a
              href={appData.links.playStore}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-cta"
            >
              <Button className="btn-primary">Get App</Button>
            </a>
            <Drawer
              open={isDrawerOpen}
              onOpenChange={setIsDrawerOpen}
              direction="bottom"
            >
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="mobile-menu-btn">
                  <Menu size={24} />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="mobile-drawer-content">
                {/* <DrawerHeader>
                  <DrawerTitle>Menu</DrawerTitle>
                </DrawerHeader> */}
                <div className="mobile-nav-links">
                  <a
                    href="#features"
                    className="mobile-nav-link"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    Features
                  </a>
                  <a
                    href="#how-it-works"
                    className="mobile-nav-link"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    How It Works
                  </a>
                  <a
                    href="#download"
                    className="mobile-nav-link"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    Download
                  </a>
                  <Link
                    to="/privacy"
                    className="mobile-nav-link"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    Privacy
                  </Link>
                  <a
                    href={appData.links.playStore}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mobile-nav-link mobile-nav-cta"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    Get App
                  </a>
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Save WhatsApp Statuses{" "}
              <span className="text-accent">Before They Vanish</span>
            </h1>
            <p className="hero-subtitle">
              Keep your favorite WhatsApp photos and videos forever. Fast,
              simple, and no login required.
            </p>
            <div className="hero-cta">
              <a
                href={appData.links.playStore}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="btn-primary btn-large">
                  <Download className="btn-icon" />
                  Download on Play Store
                </Button>
              </a>
              <a
                href={appData.links.github}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="btn-secondary btn-large">
                  <Github className="btn-icon" />
                  View on GitHub
                </Button>
              </a>
            </div>
            <p className="hero-note">
              Free & Open Source • No Ads • Privacy Focused
            </p>
          </div>
          <div className="hero-image">
            <div className="phone-mockup">
              <img
                src={appData.screenshots[0].url}
                alt="Status Saver App"
                className="mockup-screen"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Status Saver?</h2>
            <p className="section-subtitle">
              Everything you need to save and manage WhatsApp statuses
            </p>
          </div>
          <div className="features-grid">
            {appData.features.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="feature-card">
                  <div className="feature-icon">
                    <IconComponent size={24} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Save any WhatsApp status in just 3 simple steps
            </p>
          </div>
          <div className="steps-container">
            {appData.howItWorks.map((step, index) => (
              <React.Fragment key={step.step}>
                <div className="step-card">
                  <div className="step-number">{step.step}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
                {index < appData.howItWorks.length - 1 && (
                  <ChevronRight className="step-arrow" size={24} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="screenshots-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">App Preview</h2>
            <p className="section-subtitle">
              Experience the clean and intuitive interface
            </p>
          </div>
          <div className="screenshots-grid">
            {appData.screenshots.map((screenshot) => (
              <div key={screenshot.id} className="screenshot-card">
                <img
                  src={screenshot.url}
                  alt={screenshot.alt}
                  className="screenshot-img"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download CTA Section */}
      <section id="download" className="download-section">
        <div className="download-content">
          <h2 className="download-title">
            Ready to Save Your Favorite Moments?
          </h2>
          <p className="download-subtitle">
            Download Status Saver now and never lose a WhatsApp status again
          </p>
          <div className="download-buttons">
            <a
              href={appData.links.playStore}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="btn-primary btn-large">
                <Download className="btn-icon" />
                Get it on Google Play
              </Button>
            </a>
          </div>
          <div className="download-info">
            <div className="info-item">
              <Shield size={20} />
              <span>100% Free</span>
            </div>
            <div className="info-item">
              <Lock size={20} />
              <span>No Login Required</span>
            </div>
            <div className="info-item">
              <Github size={20} />
              <span>Open Source</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <img
                src={appData.logoUrl}
                alt="Status Saver"
                className="footer-logo-img"
              />
              <span className="footer-logo-text">{appData.name}</span>
            </div>
            <p className="footer-description">
              Save WhatsApp statuses easily. Free, fast, and open source.
            </p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Links</h4>
            <ul className="footer-links">
              <li>
                <a
                  href={appData.links.playStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  <ExternalLink size={16} />
                  Play Store
                </a>
              </li>
              <li>
                <a
                  href={appData.links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  <Github size={16} />
                  GitHub
                </a>
              </li>
              <li>
                <Link to="/privacy" className="footer-link">
                  <ExternalLink size={16} />
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Legal</h4>
            <p className="footer-disclaimer">
              This app is not affiliated with WhatsApp. Please respect others'
              rights and ask permission before reposting statuses.
            </p>
            <p className="footer-license">Licensed under GPL-3.0</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Status Saver. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
